import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import { Play, Clock, BookOpen, Star, Grid, List } from 'lucide-react';
import ImageWithFallback from '../../components/ImageWithFallback';

const MyCourses = () => {
    const navigate = useNavigate();
    const [purchasedCourses, setPurchasedCourses] = useState([]);
    const [viewMode, setViewMode] = useState('grid');

    useEffect(() => {
        const fetchPurchasedCourses = async () => {
            try {
                const { getUserEnrollments } = require('../../services/api');
                const result = await getUserEnrollments();
                if (result.success) {
                    setPurchasedCourses(result.data);
                }
            } catch (error) {
                console.error("Error fetching purchased courses:", error);
                const purchased = JSON.parse(localStorage.getItem('purchasedCourses') || '[]');
                setPurchasedCourses(purchased);
            }
        };

        fetchPurchasedCourses();
    }, []);

    const handleCourseClick = (course) => {
        if (!course) return;
        const targetId = course.course_id || course.id;
        localStorage.setItem('selectedCourse', JSON.stringify(course));
        navigate(`/courseoverview?course_id=${targetId}`);
    };

    const PurchasedCourseCard = ({ course }) => (
        <div className="bg-white rounded-xl overflow-hidden shadow-2xs border border-gray-100 hover:border-emerald-200 transition-all duration-300 flex flex-col justify-between">
            <div>
                <div className="relative h-32 sm:h-44 overflow-hidden">
                    <ImageWithFallback
                        src={course.image}
                        alt={course.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 bg-emerald-600 text-white text-[9px] sm:text-xs font-extrabold px-2 py-0.5 rounded-full shadow-2xs">
                        Enrolled
                    </div>
                </div>

                <div className="p-2.5 sm:p-4 space-y-2">
                    <div className="flex items-center gap-1.5">
                        <span className="text-[9px] sm:text-xs font-bold px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-100">
                            {course.department || 'Technical'}
                        </span>
                        {course.level && (
                            <span className="text-[9px] sm:text-xs font-bold px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-100">
                                {course.level}
                            </span>
                        )}
                    </div>

                    <h3 className="font-bold text-gray-900 text-xs sm:text-sm line-clamp-2 leading-snug">
                        {course.title}
                    </h3>

                    <p className="text-[10px] sm:text-xs text-gray-500 font-medium truncate">
                        {course.instructor || 'Specialist Instructor'}
                    </p>

                    <div className="flex items-center gap-2 text-[10px] sm:text-xs">
                        <div className="flex items-center gap-0.5 text-amber-500">
                            <Star className="fill-current text-amber-400" size={12} />
                            <span className="font-bold text-gray-800">{course.rating || '4.9'}</span>
                        </div>
                        <span className="text-gray-300">•</span>
                        <span className="text-gray-400 truncate">Verified Course</span>
                    </div>

                    <div className="flex items-center gap-2 text-[10px] text-gray-400 pt-0.5">
                        <span className="flex items-center gap-1">
                            <Clock size={11} />
                            {course.duration || '10 Hours'}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                            <BookOpen size={11} />
                            {course.lectures || 12} Lessons
                        </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="pt-1 space-y-1">
                        <div className="flex justify-between text-[10px] font-bold">
                            <span className="text-gray-500">Progress</span>
                            <span className="text-emerald-700">{course.progress || 0}%</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                            <div
                                className="bg-emerald-600 h-full rounded-full transition-all duration-300"
                                style={{ width: `${course.progress || 0}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-2.5 sm:p-4 pt-0">
                <button
                    onClick={() => handleCourseClick(course)}
                    className="w-full bg-emerald-600 text-white py-1.5 sm:py-2 rounded-lg text-xs font-bold hover:bg-emerald-700 transition flex items-center justify-center gap-1.5 shadow-2xs cursor-pointer"
                >
                    <Play size={12} className="fill-current" />
                    <span>{Number(course.progress) >= 99 ? 'Review Course' : 'Continue Learning'}</span>
                </button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50/50 flex flex-col font-sans">
            <main className="flex-1 max-w-7xl mx-auto px-2.5 sm:px-6 lg:px-8 py-3 sm:py-6 w-full space-y-3 sm:space-y-5">
                <div className="border-b border-gray-100 pb-2">
                    <h1 className="text-sm sm:text-xl font-bold text-gray-900">My Courses</h1>
                    <p className="text-[10px] sm:text-xs text-gray-500">Continue your learning journey with your enrolled courses</p>
                </div>

                {purchasedCourses.length > 0 ? (
                    <>
                        <div className="flex items-center justify-between gap-2">
                            <p className="text-[11px] sm:text-xs text-gray-600 font-medium">
                                Enrolled in <span className="font-bold text-gray-900">{purchasedCourses.length}</span> course{purchasedCourses.length !== 1 ? 's' : ''}
                            </p>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-1.5 rounded-lg text-xs transition cursor-pointer ${viewMode === 'grid' ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-gray-600'}`}
                                    title="Grid View"
                                >
                                    <Grid size={14} />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-1.5 rounded-lg text-xs transition cursor-pointer ${viewMode === 'list' ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-gray-600'}`}
                                    title="List View"
                                >
                                    <List size={14} />
                                </button>
                            </div>
                        </div>

                        <div className={`grid gap-2.5 sm:gap-4 ${viewMode === 'grid'
                            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                            : 'grid-cols-1'
                            }`}>
                            {purchasedCourses.map((course) => (
                                <PurchasedCourseCard key={course.id || course.course_id} course={course} />
                            ))}
                        </div>
                    </>
                ) : (
                    /* Compact Mobile Empty State Card */
                    <div className="bg-white rounded-xl p-4 sm:p-10 text-center shadow-2xs border border-gray-100 space-y-2.5 max-w-md mx-auto my-4">
                        <div className="w-10 h-10 sm:w-14 sm:h-14 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                            <BookOpen size={20} className="sm:hidden" />
                            <BookOpen size={28} className="hidden sm:block" />
                        </div>
                        <div>
                            <h3 className="text-xs sm:text-base font-bold text-gray-900">No Courses Purchased Yet</h3>
                            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 leading-relaxed">
                                Start your learning journey by purchasing your first course from our catalog.
                            </p>
                        </div>
                        <button
                            onClick={() => navigate('/')}
                            className="bg-emerald-600 text-white px-4 py-2 sm:px-6 sm:py-2.5 rounded-lg text-xs font-bold hover:bg-emerald-700 transition shadow-2xs cursor-pointer inline-flex items-center gap-1"
                        >
                            Browse Courses
                        </button>
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
};

export default MyCourses;
